import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUniversalLibrary } from '@/contexts/UniversalLibraryContext';
import { updateAllCachesSurgically } from './cache-update-utils';
import { UNIVERSAL_CACHE_KEYS } from './cache-keys';

const getUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated.");
    return user.id;
};

// Helper to get the correct foreign key column based on content type
const getForeignKeyColumn = (contentType: string): string => {
    switch (contentType) {
        case 'private_image':
            return 'workbook_image_id';
        case 'private_research':
            return 'workbook_research_id';
        case 'private_page':
            return 'workbook_page_id';
        default:
            throw new Error(`Unknown content type: ${contentType}`);
    }
};

// Hook to get the single category for a specific library item
export const useItemCategory = (itemId: string | null, contentType: string) => {
    return useQuery({
        queryKey: UNIVERSAL_CACHE_KEYS.ITEM_CATEGORY(itemId || '', contentType),
        queryFn: async () => {
            if (!itemId) return null;
            const userId = await getUserId();
            const foreignKeyColumn = getForeignKeyColumn(contentType);
            
            const { data, error } = await supabase
                .from('universal_storage_items')
                .select('category_id')
                .eq('user_id', userId)
                .eq(foreignKeyColumn, itemId)
                .eq('content_type', contentType)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    // No rows found - item is uncategorized
                    return null;
                }
                toast.error('Failed to fetch item category.');
                throw error;
            }
            return data.category_id;
        },
        enabled: !!itemId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Hook for mutations (setting/removing category)
export const useItemCategoryMutations = (itemId: string, contentType: string) => {
    const queryClient = useQueryClient();
    const { triggerRefetch } = useUniversalLibrary();

    const updateCachesSurgically = (categoryId: string | null) => {
        // Use the new comprehensive cache update system
        updateAllCachesSurgically({
            queryClient,
            itemId,
            contentType,
            categoryId,
            triggerRefetch,
        });
    };

    const setCategory = useMutation({
        mutationFn: async (categoryId: string | null) => {
            const userId = await getUserId();
            
            if (categoryId === null) {
                // Remove from all categories (set as uncategorized)
                const foreignKeyColumn = getForeignKeyColumn(contentType);
                const { error } = await supabase
                    .from('universal_storage_items')
                    .delete()
                    .eq(foreignKeyColumn, itemId)
                    .eq('content_type', contentType)
                    .eq('user_id', userId);

                if (error) {
                    toast.error(`Failed to remove from category: ${error.message}`);
                    throw error;
                }
            } else {
                // First, remove from any existing category
                const foreignKeyColumn = getForeignKeyColumn(contentType);
                await supabase
                    .from('universal_storage_items')
                    .delete()
                    .eq(foreignKeyColumn, itemId)
                    .eq('content_type', contentType)
                    .eq('user_id', userId);

                // Get the current max display_order for this category and content type
                const { data: maxOrderData, error: maxOrderError } = await supabase
                    .from('universal_storage_items')
                    .select('display_order')
                    .eq('category_id', categoryId)
                    .eq('content_type', contentType)
                    .eq('user_id', userId)
                    .order('display_order', { ascending: false })
                    .limit(1);

                if (maxOrderError) {
                    console.warn('Failed to get max display order, using default:', maxOrderError);
                }

                // Calculate new display_order (add to end) - ensure it's always >= 1000
                const maxOrder = maxOrderData && maxOrderData.length > 0 && maxOrderData[0].display_order 
                    ? maxOrderData[0].display_order 
                    : 0;
                const newDisplayOrder = Math.max(maxOrder + 1000, 1000);

                // Then add to the new category with proper display_order
                const insertData: any = {
                    [foreignKeyColumn]: itemId,
                    category_id: categoryId,
                    user_id: userId,
                    content_type: contentType,
                    display_order: newDisplayOrder,
                };
                
                const { error } = await supabase
                    .from('universal_storage_items')
                    .insert(insertData);

                if (error) {
                    toast.error(`Failed to set category: ${error.message}`);
                    throw error;
                }
            }
        },
        onSuccess: (_, categoryId) => {
            if (categoryId === null) {
                toast.success('Removed from category.');
            } else {
                toast.success('Category updated.');
            }
            updateCachesSurgically(categoryId);
        },
    });

    return {
        setCategory: setCategory.mutateAsync,
        isPending: setCategory.isPending,
    };
};

// Legacy hook for backward compatibility - now returns array with single item or empty
export const useItemCategories = (itemId: string | null, contentType: string) => {
    const { data: categoryId } = useItemCategory(itemId, contentType);
    return {
        data: categoryId ? [categoryId] : [],
        isLoading: false,
    };
};
